<script setup lang="ts">
import { NuxtLink } from '#components';
import { cn } from '~/utils';

export interface ImageConfig {
	/**
	 * URL ou caminho da imagem de fundo do card
	 */
	src: string;
	/**
	 * Texto alternativo para a imagem
	 */
	alt: string;
	/**
	 * Qualidade da imagem (1-100)
	 * @default 90
	 */
	quality?: number;
	/**
	 * Se true, a imagem será carregada com lazy loading
	 * @default true
	 */
	lazy?: boolean;
}

export interface ContentConfig {
	/**
	 * Título principal do card
	 */
	title: string;
	/**
	 * Subtítulo ou descrição do card (opcional)
	 */
	subtitle?: string;
}

export interface StyleConfig {
	/**
	 * Classes customizadas para o container do card
	 */
	container?: string;
	/**
	 * Classes customizadas para o título
	 */
	title?: string;
	/**
	 * Classes customizadas para o subtítulo
	 */
	subtitle?: string;
}

export interface CardImageOverlayProps {
	/**
	 * Configurações da imagem
	 */
	image: ImageConfig;
	/**
	 * Configurações do conteúdo (título e subtítulo)
	 */
	content: ContentConfig;
	/**
	 * URL de destino do link (opcional)
	 */
	to?: string;
	/**
	 * Se true, mostra o ícone de seta no canto superior direito
	 * @default true
	 */
	showArrowIcon?: boolean;
	/**
	 * Classes customizadas para elementos do card
	 */
	styleConfig?: StyleConfig;
}

const props = withDefaults(defineProps<CardImageOverlayProps>(), {
	showArrowIcon: true
});

const emit = defineEmits<{
	/**
	 * Emitido quando o card é clicado
	 */
	click: [event: MouseEvent];
}>();

function handleClick(event: MouseEvent): void {
	emit('click', event);
}

const imageQuality = computed(() => props.image.quality ?? 75);
const imageLazy = computed(() => props.image.lazy ?? true);

const defaultContainerClass =
	'group relative size-full cursor-pointer overflow-hidden text-white flex';

const defaultTitleClass =
	'font-sans text-xl font-medium leading-normal tracking-[-0.025rem] xl:text-2xl';

const defaultSubtitleClass =
	'font-sans text-base font-medium leading-relaxed text-white-dark-2 transition-[max-height,transform,opacity] duration-500 ease-in-out [text-shadow:0_1px_3px_rgba(0,0,0,0.15)] lg:max-h-0 lg:translate-y-2 lg:text-lg lg:leading-[150%] lg:tracking-normal lg:opacity-0 lg:group-hover:max-h-20 lg:group-hover:translate-y-0 lg:group-hover:opacity-100';

const componentTag = computed(() => (props.to ? NuxtLink : 'div'));
const componentProps = computed(() => (props.to ? { to: props.to } : {}));
</script>

<template>
	<component
		:is="componentTag"
		v-bind="componentProps"
		:class="cn(defaultContainerClass, styleConfig?.container)"
		@click="handleClick"
	>
		<NuxtImg
			:src="image.src"
			:alt="image.alt"
			width="612"
			height="612"
			sizes="xs:375px sm:320px md:256px lg:345px"
			densities="x1"
			class="absolute inset-0 flex size-full object-cover transition-transform duration-500 ease-in-out lg:group-hover:scale-105"
			:loading="imageLazy ? 'lazy' : 'eager'"
			format="webp"
			:quality="imageQuality"
		/>

		<div
			class="relative flex size-full flex-col justify-end bg-linear-to-b from-transparent from-0% to-black/20 to-50% p-4 xl:p-5"
		>
			<div
				class="flex flex-col gap-2 transition-transform duration-500 ease-in-out lg:group-hover:-translate-y-2"
			>
				<div
					class="flex items-center gap-3 [text-shadow:0_1px_3px_rgba(0,0,0,0.15)]"
				>
					<span :class="cn(defaultTitleClass, styleConfig?.title)">
						{{ content.title }}
					</span>

					<IconMaterialSymbolsNorthEast v-if="showArrowIcon" class="size-5" />
				</div>

				<p
					v-if="content.subtitle"
					:class="cn(defaultSubtitleClass, styleConfig?.subtitle)"
				>
					{{ content.subtitle }}
				</p>
			</div>
		</div>
	</component>
</template>
